import React from 'react';
import { Box, Chip, Paper, Stack, Tooltip, Typography } from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { ReliabilityTrace } from '../../services/reliabilityApi';

const MODE_SETTING: Record<string, string> = {
  standard: 'standard retries',
  fail: 'return the error right away',
  retry: 'retry the same model',
  fallback: 'switch to a healthy backup model',
};

const RESULT: Record<string, { label: string; color: 'success' | 'error' | 'warning' }> = {
  ok: { label: 'Answered', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
  time_limit: { label: 'Time limit reached', color: 'warning' },
};

const OUTCOME_LABEL: Record<string, string> = {
  ok: 'answered',
  timeout: 'timed out',
  time_limit: 'hit the time limit',
  cancelled: 'cancelled (another attempt answered first)',
  skipped: 'skipped (marked unhealthy)',
  rate_limit: 'rate limited',
  server: 'provider error',
  auth: 'auth error',
  bad_output: 'returned invalid output',
  empty: 'returned an empty response',
  input: 'rejected the request',
  media_url: 'could not load a media URL',
};

const OUTCOME_COLOR: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  ok: 'success',
  timeout: 'warning',
  time_limit: 'warning',
  cancelled: 'default',
  skipped: 'default',
};

const secs = (s?: number | null) => (s === undefined || s === null ? '–' : `${s.toFixed(1)}s`);
const outcomeText = (o: string) => OUTCOME_LABEL[o] || o.replace(/_/g, ' ');

export interface ModelRoute {
  requested_model?: string | null;
  selected_model?: string | null;
  model_used?: string | null;
  model_change_reason?: 'zdr_substitute' | 'reliability_fallback' | null;
}

/** One sentence on why the model that ran differs from the one requested. */
export function modelChangeExplanation(route: ModelRoute): string {
  const { requested_model: requested, selected_model: selected, model_used: ran } = route;
  if (route.model_change_reason === 'zdr_substitute') {
    const swap = `${requested} isn't allowed under this account's Zero Data Retention settings, so ${selected} was used instead.`;
    return ran && ran !== selected ? `${swap} ${selected} then failed and the reliability setting switched to ${ran}.` : swap;
  }
  if (route.model_change_reason === 'reliability_fallback') {
    return `${selected || requested} failed or was too slow, so the function's reliability setting switched to ${ran}, which answered.`;
  }
  return '';
}

/** Plain-English account of one call: which model answered, after what, within which limit. */
export function reliabilitySummary(trace: ReliabilityTrace): string {
  const attempts = trace.attempts || [];
  const winner = attempts.find(a => a.outcome === 'ok');
  const failures = attempts.filter(a => a.outcome !== 'ok' && a.outcome !== 'cancelled' && a.outcome !== 'skipped');
  const took = trace.elapsed_s !== undefined && trace.elapsed_s !== null ? ` in ${secs(trace.elapsed_s)}` : '';
  const limit = trace.time_limit_s ? ` (time limit ${secs(trace.time_limit_s)})` : '';

  if (trace.result === 'ok' && winner) {
    if (!failures.length) {
      const hedged = winner.hedge ? ' A backup was started in parallel because the first model was slow, and it answered first.' : '';
      return `Answered by ${winner.model} on the first try${took}${limit}.${hedged}`;
    }
    const failed = failures.map(f => `${f.model} ${outcomeText(f.outcome)}`).join(', then ');
    const sameModel = failures.every(f => f.model === winner.model);
    return sameModel
      ? `${failed}; a retry of ${winner.model} answered${took}${limit}.`
      : `${failed}, so it switched to ${winner.model}, which answered${took}${limit}.`;
  }
  if (trace.result === 'time_limit') {
    return `No model answered before the ${secs(trace.time_limit_s)} time limit${attempts.length ? ` (${attempts.length} attempt${attempts.length === 1 ? '' : 's'})` : ''}.`;
  }
  if (!attempts.length) return 'The call failed before any model was tried.';
  return `All ${attempts.length} attempt${attempts.length === 1 ? '' : 's'} failed${took}.`;
}

/** What the reliability settings did during one call: attempts, models, time limit. */
export const ReliabilityTraceView: React.FC<{ trace: ReliabilityTrace; title?: string }> = ({ trace, title }) => {
  const result = RESULT[trace.result] || RESULT.failed;
  const attempts = trace.attempts || [];
  const showAttempts = attempts.length > 1 || trace.result !== 'ok' || trace.hedged_after_s !== undefined;
  const backups = (trace.plan || []).slice(1);
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <ShieldOutlinedIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2">{title || 'Reliability'}</Typography>
        <Chip size="small" color={result.color} label={result.label} />
        {trace.source === 'chain' && <Chip size="small" variant="outlined" label="Chain settings" />}
      </Stack>

      <Typography variant="body2" sx={{ mt: 1 }}>
        {reliabilitySummary(trace)}
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary">
          If a model fails: {MODE_SETTING[trace.mode] || trace.mode}
          {trace.mode === 'fallback' && backups.length ? ` (backups: ${backups.join(', ')})` : ''}
        </Typography>
        {trace.time_limit_s ? (
          <Tooltip title={trace.time_limit_note || ''}>
            <Typography variant="caption" color="text.secondary" sx={{ cursor: trace.time_limit_note ? 'help' : undefined }}>
              Time limit: {secs(trace.time_limit_s)}
            </Typography>
          </Tooltip>
        ) : null}
      </Stack>

      {showAttempts && attempts.length > 0 && (
        <Box component="ol" sx={{ m: 0, mt: 1.5, pl: 3 }}>
          {attempts.map((a, i) => (
            <Box component="li" key={i} sx={{ mb: 0.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {a.model}
                </Typography>
                <Chip size="small" color={OUTCOME_COLOR[a.outcome] || 'error'} label={outcomeText(a.outcome)} />
                <Typography variant="caption" color="text.secondary">
                  {secs(a.seconds)}
                  {a.hedge ? ` · backup started in parallel after ${secs(trace.hedged_after_s)}` : ''}
                  {a.fixed ? ' · output repaired' : ''}
                </Typography>
              </Stack>
              {a.error && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                  {a.error.length > 240 ? `${a.error.slice(0, 240)}…` : a.error}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
      {trace.note && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {trace.note}
        </Typography>
      )}
    </Paper>
  );
};

export default ReliabilityTraceView;
